import { dangerSummary } from "@/utils/dangers";

// Québec Eau Vive was called Quebec Whitewater (still its domain and its English name): the old name stays in the
// descriptions and the structured data so searches for it still find the site.
const SITE_NAME = "Québec Eau Vive";
const FORMER_SITE_NAMES = ["Quebec Whitewater", "Québec Whitewater"];
const DEFAULT_TITLE = "Québec Eau Vive | Quebec Whitewater";
const DEFAULT_DESCRIPTION =
  "Québec Eau Vive (anciennement Quebec Whitewater) — la référence en info-débit pour les rivières sportives du Québec. Niveaux, tendances et recommandations de débit pour planifier vos sorties en kayak et canot.";
const DEFAULT_IMAGE_PATH = "/images/home-hero.jpg";
const DEFAULT_ROBOTS = "index,follow";
const FALLBACK_SITE_URL = "https://quebecwhitewater.com";
const STRUCTURED_DATA_ELEMENT_ID = "seo-structured-data";
const PRERENDER_READY_EVENT = "qww-prerender-ready";

function stripTrailingSlash(value = "") {
  return value.replace(/\/$/, "");
}

function sanitizeText(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getSiteUrl() {
  if (process.env.VUE_APP_SITE_URL) {
    return stripTrailingSlash(process.env.VUE_APP_SITE_URL);
  }

  return FALLBACK_SITE_URL;
}

function buildAbsoluteUrl(path = "/") {
  const baseUrl = `${getSiteUrl()}/`;
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  return new URL(normalizedPath, baseUrl).toString();
}

function buildDocumentTitle(title) {
  const cleanTitle = sanitizeText(title);
  if (!cleanTitle || cleanTitle === SITE_NAME || cleanTitle === DEFAULT_TITLE) {
    return DEFAULT_TITLE;
  }

  return `${cleanTitle} | ${SITE_NAME}`;
}

function compactObject(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => compactObject(entry))
      .filter((entry) => {
        if (entry === undefined || entry === null) {
          return false;
        }

        if (typeof entry === "string") {
          return entry.trim() !== "";
        }

        if (Array.isArray(entry)) {
          return entry.length > 0;
        }

        if (typeof entry === "object") {
          return Object.keys(entry).length > 0;
        }

        return true;
      });
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, entry]) => [key, compactObject(entry)])
        .filter(([, entry]) => {
          if (entry === undefined || entry === null) {
            return false;
          }

          if (typeof entry === "string") {
            return entry.trim() !== "";
          }

          if (Array.isArray(entry)) {
            return entry.length > 0;
          }

          if (typeof entry === "object") {
            return Object.keys(entry).length > 0;
          }

          return true;
        }),
    );
  }

  return value;
}

function upsertMetaTag(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLinkTag(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function removeStructuredData() {
  const existingElement = document.getElementById(STRUCTURED_DATA_ELEMENT_ID);
  if (existingElement) {
    existingElement.remove();
  }
}

function upsertStructuredData(structuredData) {
  if (!structuredData || (Array.isArray(structuredData) && structuredData.length === 0)) {
    removeStructuredData();
    return;
  }

  let payload = structuredData;
  if (Array.isArray(structuredData)) {
    payload = {
      "@context": "https://schema.org",
      "@graph": structuredData.map((entry) => {
        if (entry && typeof entry === "object" && entry["@context"]) {
          const { ["@context"]: _context, ...rest } = entry;
          return rest;
        }
        return entry;
      }),
    };
  } else if (!structuredData["@context"]) {
    payload = {
      "@context": "https://schema.org",
      ...structuredData,
    };
  }

  payload = compactObject(payload);

  let element = document.getElementById(STRUCTURED_DATA_ELEMENT_ID);
  if (!element) {
    element = document.createElement("script");
    element.id = STRUCTURED_DATA_ELEMENT_ID;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(payload);
}

function buildSectionDescription(section = {}) {
  const river = sanitizeText(section.river);
  const sectionName = sanitizeText(section.name);
  const region = sanitizeText(section.region);
  const minLevel = sanitizeText(section.min_sug_level);
  const maxLevel = sanitizeText(section.max_sug_level);
  const descriptionSource = sanitizeText(section.description);
  const descriptionParts = [];

  if (river || sectionName) {
    descriptionParts.push([river, sectionName].filter(Boolean).join(" - "));
  }
  if (region) {
    descriptionParts.push(`Région ${region}`);
  }
  if (minLevel && maxLevel) {
    descriptionParts.push(`Débit recommandé entre ${minLevel} et ${maxLevel} mcs`);
  }
  if (descriptionSource) {
    descriptionParts.push(descriptionSource);
  }

  return descriptionParts.join(". ").slice(0, 300) || DEFAULT_DESCRIPTION;
}

export function applySeoMeta(options = {}) {
  if (typeof document === "undefined") {
    return;
  }

  const description = sanitizeText(options.description) || DEFAULT_DESCRIPTION;
  const title = buildDocumentTitle(options.title);
  const canonicalPath = options.canonicalPath || (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonicalUrl = buildAbsoluteUrl(canonicalPath);
  const imageUrl = buildAbsoluteUrl(options.imagePath || DEFAULT_IMAGE_PATH);
  const robots = options.robots || DEFAULT_ROBOTS;
  const ogType = options.ogType || "website";

  document.title = title;
  document.documentElement.setAttribute("lang", options.lang || "fr-CA");

  upsertMetaTag('meta[name="description"]', { name: "description", content: description });
  upsertMetaTag('meta[name="robots"]', { name: "robots", content: robots });
  upsertMetaTag('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
  upsertMetaTag('meta[property="og:title"]', { property: "og:title", content: title });
  upsertMetaTag('meta[property="og:description"]', { property: "og:description", content: description });
  upsertMetaTag('meta[property="og:type"]', { property: "og:type", content: ogType });
  upsertMetaTag('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
  upsertMetaTag('meta[property="og:image"]', { property: "og:image", content: imageUrl });
  upsertMetaTag('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  upsertMetaTag('meta[name="twitter:title"]', { name: "twitter:title", content: title });
  upsertMetaTag('meta[name="twitter:description"]', { name: "twitter:description", content: description });
  upsertMetaTag('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });
  upsertLinkTag('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });
  upsertStructuredData(options.structuredData);
}

export function buildHomeStructuredData() {
  const siteUrl = buildAbsoluteUrl("/");

  return [
    {
      "@type": "Organization",
      name: SITE_NAME,
      alternateName: FORMER_SITE_NAMES,
      url: siteUrl,
      logo: buildAbsoluteUrl("/img/icons/icon-512.png"),
      sameAs: ["https://www.facebook.com/quebecwhitewater"],
    },
    {
      "@type": "WebSite",
      name: SITE_NAME,
      alternateName: FORMER_SITE_NAMES,
      url: siteUrl,
      inLanguage: "fr-CA",
    },
    {
      "@type": "CollectionPage",
      name: DEFAULT_TITLE,
      url: siteUrl,
      description: DEFAULT_DESCRIPTION,
      inLanguage: "fr-CA",
    },
  ];
}

export function buildSectionStructuredData(section = {}, options = {}) {
  const alias = sanitizeText(section.alias);
  if (!alias) {
    return [];
  }

  const river = sanitizeText(section.river);
  const sectionName = sanitizeText(section.name);
  const region = sanitizeText(section.region);
  const classLevel = sanitizeText(section.class_level);
  const classLevelMax = sanitizeText(section.class_level_max);
  const description = sanitizeText(options.description) || buildSectionDescription(section);
  const pageUrl = buildAbsoluteUrl(`/riviere/${alias}`);
  const sectionTitle = [river, sectionName].filter(Boolean).join(" - ") || "Conditions de rivière";
  const classLabel = classLevelMax ? `${classLevel}-${classLevelMax}` : classLevel;

  return [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Accueil",
          item: buildAbsoluteUrl("/"),
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Rivières",
          item: buildAbsoluteUrl("/rivieres"),
        },
        {
          "@type": "ListItem",
          position: 3,
          name: sectionTitle,
          item: pageUrl,
        },
      ],
    },
    {
      "@type": "WebPage",
      name: sectionTitle,
      url: pageUrl,
      description,
      inLanguage: "fr-CA",
    },
    {
      "@type": "SportsActivityLocation",
      name: sectionTitle,
      description,
      url: pageUrl,
      sport: "Whitewater kayaking",
      isAccessibleForFree: section.free_access === true,
      address: region
        ? {
            "@type": "PostalAddress",
            addressRegion: region,
            addressCountry: "CA",
          }
        : undefined,
      additionalType: classLabel ? `Classe ${classLabel}` : undefined,
    },
  ];
}

export function signalPrerenderReady() {
  if (typeof document === "undefined") {
    return;
  }

  document.dispatchEvent(new Event(PRERENDER_READY_EVENT));
}

export function buildRouteSeo(route) {
  const routeMeta =
    [...(route.matched || [])]
      .reverse()
      .map((record) => record.meta || {})
      .find((meta) => meta.title || meta.description || meta.robots) || {};

  return {
    title: routeMeta.title,
    description: routeMeta.description,
    robots: routeMeta.robots,
    canonicalPath: route.path || "/",
  };
}

export function buildSectionSeo(section = {}, options = {}) {
  const alias = sanitizeText(section.alias);
  const river = sanitizeText(section.river);
  const sectionName = sanitizeText(section.name);
  const classLevel = sanitizeText(section.class_level);
  const classLevelMax = sanitizeText(section.class_level_max);
  const isFreeAccess = section.free_access === true;
  const hasAccess = options.hasAccess === true;
  const activeDangers = (options.dangers || []).filter((danger) => danger && !danger.archived);

  const titleParts = [river, sectionName].filter(Boolean);
  let title = titleParts.join(" - ") || "Conditions de rivière";

  if (classLevel) {
    title += classLevelMax ? ` | Classe ${classLevel}-${classLevelMax}` : ` | Classe ${classLevel}`;
  }

  // Active dangers come first: they are what someone searching for this river right now needs to see.
  const dangersText = activeDangers.length
    ? `Danger${activeDangers.length > 1 ? "s" : ""} signalé${activeDangers.length > 1 ? "s" : ""} : ${activeDangers
        .map(dangerSummary)
        .join(" ; ")}`
    : "";
  const description = dangersText
    ? `${dangersText}. ${buildSectionDescription(section)}`.slice(0, 300)
    : buildSectionDescription(section);
  // Members-only sections are indexed while they have active dangers: their public page then shows the dangers.
  const robots = (isFreeAccess && hasAccess) || activeDangers.length ? DEFAULT_ROBOTS : "noindex,nofollow";

  return {
    title,
    description,
    robots,
    canonicalPath: alias ? `/riviere/${alias}` : "/rivieres",
    ogType: "article",
    structuredData: buildSectionStructuredData(section, { description }),
  };
}

export { DEFAULT_DESCRIPTION, DEFAULT_TITLE, FALLBACK_SITE_URL, SITE_NAME, PRERENDER_READY_EVENT };
